import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const cat = article ? categories.find((c) => c.slug === article.category) : null;

  const related = article
    ? articles.filter((a) => a.category === article.category && a.slug !== article.slug).slice(0, 3)
    : [];

  useEffect(() => {
    if (!article) return;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { '@type': 'Organization', name: '鲍鱼网址域名导航' },
      publisher: { '@type': 'Organization', name: '鲍鱼网址域名导航' },
      url: `https://baoyu.com/article/${article.slug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: '鲍鱼网址域名导航',
        url: 'https://baoyu.com',
      },
    });
    document.head.appendChild(script);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.innerHTML = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoyu.com/' },
        { '@type': 'ListItem', position: 2, name: cat?.name || '分类', item: `https://baoyu.com/category/${article.category}` },
        { '@type': 'ListItem', position: 3, name: article.title, item: `https://baoyu.com/article/${article.slug}` },
      ],
    });
    document.head.appendChild(breadcrumbScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(breadcrumbScript);
    };
  }, [article, cat]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <p className="text-foreground-500">文章未找到</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[
          { label: cat?.name || '分类', href: `/category/${article.category}` },
          { label: article.title },
        ]} />

        <article className="px-4 md:px-6 py-8 md:py-10 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">{tag}</span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight">{article.title}</h1>
            <div className="flex items-center gap-4 mt-4 text-sm text-foreground-400">
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                <span>{article.readTime}</span>
              </div>
              {cat && (
                <Link to={`/category/${cat.slug}`} className="flex items-center gap-1 text-primary-500 hover:text-primary-600 transition-colors">
                  <i className={cat.icon}></i>
                  <span>{cat.name}</span>
                </Link>
              )}
            </div>

            <div className="mt-8 prose prose-sm max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />
          </div>
        </article>

        {/* Related Articles */}
        {related.length > 0 && (
          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map((ra) => (
                  <Link key={ra.slug} to={`/article/${ra.slug}`} className="group bg-background-50 rounded-lg p-4 transition-all duration-300 hover:-translate-y-1">
                    <h4 className="font-heading font-semibold text-foreground-900 text-sm leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">{ra.title}</h4>
                    <div className="mt-2 flex items-center gap-1 text-xs text-foreground-400">
                      <i className="ri-time-line"></i> {ra.readTime}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}